import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { INK, NEUTRAL } from "../../../theme/tokens";

export type SwatchLegendItem = {
	/** Stable react key. */
	id: string;
	/** Already-translated label. */
	label: string;
	/** Swatch fill. Omit when the item is keyed by its `glyph` alone. */
	fill?: string;
	/** Left accent rule inside the swatch — mirrors `CoverageDeltaCell`'s severity bar. */
	rail?: string;
	/** Render the swatch as a dot instead of the default rounded square (a category, not a state). */
	round?: boolean;
	/** Leading glyph, drawn inside the swatch when there is a fill and standalone when there isn't. */
	glyph?: ReactNode;
};

type Props = {
	/** Optional leading caption, e.g. "Key". Already translated. */
	title?: string;
	items: readonly SwatchLegendItem[];
	/** 12px swatches + 11px labels, for a legend sharing a toolbar row rather than owning one. */
	dense?: boolean;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A row of swatch + label keys for whatever a nearby grid encodes with color, position or a glyph.
 *
 * It exists so a dense grid says what its colors mean ONCE instead of repeating a label in every cell —
 * and so every legend in the app is built the same way. The caller owns the items (and derives them from
 * the same source the cells render from, so the two can never drift); this owns the layout and type
 * scale. Purely presentational: no interaction, no page data.
 */
export const SwatchLegend: FC<Props> = ({ title, items, dense = false, sx, dataTestId }) => {
	const swatchSize = dense ? 12 : 16;
	const fontSize = dense ? "11px" : "12px";

	return (
		<Box data-testid={dataTestId} sx={[{ display: "flex", alignItems: "center", gap: dense ? 1 : 2, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
			{title !== undefined && <Typography sx={{ fontSize, fontWeight: 600, color: INK.MUTED, whiteSpace: "nowrap" }}>{title}</Typography>}
			{items.map((item) => (
				<Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
					{item.fill === undefined ? (
						item.glyph
					) : (
						<Box
							aria-hidden
							sx={{
								position: "relative",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: item.round ? swatchSize - 4 : swatchSize,
								height: item.round ? swatchSize - 4 : swatchSize,
								flexShrink: 0,
								borderRadius: item.round ? "50%" : 0.75,
								backgroundColor: item.fill,
								border: item.round ? "none" : `1px solid ${NEUTRAL[200]}`,
								overflow: "hidden",
							}}
						>
							{item.rail !== undefined && <Box sx={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "3px", backgroundColor: item.rail }} />}
							{item.glyph}
						</Box>
					)}
					<Typography sx={{ fontSize, color: INK.SECONDARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</Typography>
				</Box>
			))}
		</Box>
	);
};
